import React, { useState, Component } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SparklesIcon, LoaderIcon } from 'lucide-react';
import { FormSection, TextField, SelectField, TextAreaField, DisplayField } from '../components/FormComponents';
import { mockProducts, masterDataSets } from '../data/mockData';
export const productHierarchy = {
  products: [{
    value: 'Laptop',
    label: 'Laptop',
    productTypes: ['Fixed Asset']
  }, {
    value: 'Printer',
    label: 'Printer',
    productTypes: ['Fixed Asset', 'Consumable']
  }, {
    value: 'Paper',
    label: 'Paper',
    productTypes: ['Consumable']
  }, {
    value: 'Cleaning Supplies',
    label: 'Cleaning Supplies',
    productTypes: ['Consumable']
  }],
  subProducts: {
    Laptop: ['Windows Laptop', 'Mac Laptop', 'Chromebook'],
    Printer: ['Laser Printer', 'Inkjet Printer'],
    Paper: ['A4 Paper', 'A3 Paper', 'Glossy Paper'],
    'Cleaning Supplies': ['Floor Cleaner', 'Glass Cleaner']
  } as Record<string, string[]>,
  items: {
    'Windows Laptop': ['Samsung', 'Dell', 'HP', 'Lenovo'],
    'Mac Laptop': ['MacBook Air', 'MacBook Pro'],
    Chromebook: ['Acer', 'HP'],
    'Laser Printer': ['HP LaserJet', 'Brother'],
    'Inkjet Printer': ['Canon', 'Epson'],
    'A4 Paper': ['80gsm', '100gsm'],
    'A3 Paper': ['80gsm', '100gsm'],
    'Glossy Paper': ['150gsm', '200gsm'],
    'Floor Cleaner': ['Pine', 'Lemon'],
    'Glass Cleaner': ['Standard', 'Streak-Free']
  } as Record<string, string[]>,
  subItems: {
    Samsung: ['Ram 16', 'Ram 8', 'SSD 512', 'SSD 256'],
    Dell: ['Ram 16', 'Ram 32', 'SSD 512'],
    HP: ['Ram 8', 'Ram 16'],
    Lenovo: ['Ram 16', 'Ram 32'],
    'MacBook Air': ['M2 8GB', 'M2 16GB'],
    'MacBook Pro': ['M3 Pro 18GB', 'M3 Max 36GB'],
    Acer: ['4GB', '8GB'],
    'HP LaserJet': ['Black', 'Color'],
    Brother: ['Black', 'Color'],
    Canon: ['Standard', 'Photo'],
    Epson: ['Standard', 'Photo'],
    '80gsm': ['White', 'Recycled'],
    '100gsm': ['White', 'Premium'],
    '150gsm': ['Glossy', 'Matte'],
    '200gsm': ['Glossy', 'Matte'],
    Pine: ['1L', '5L'],
    Lemon: ['1L', '5L'],
    Standard: ['500ml', '1L'],
    'Streak-Free': ['500ml', '1L']
  } as Record<string, string[]>
};
export function ProductForm() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const mode = id ? 'edit' : 'create';
  const initialData = mode === 'edit' && id ? mockProducts.find(p => p.id === id) : null;
  const [formData, setFormData] = useState<any>(initialData || {
    productCode: 'PROD-AUTO',
    productName: '',
    product: '',
    productType: '',
    subProduct: '',
    item: '',
    subItem: '',
    unitOfMeasure: '',
    defaultRate: '',
    warranty: '',
    status: 'Active',
    notes: ''
  });
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData((prev: any) => {
      const newData = {
        ...prev,
        [name]: value
      };
      // Reset child fields when parent changes
      if (name === 'product') {
        newData.productType = '';
        newData.subProduct = '';
        newData.item = '';
        newData.subItem = '';
      } else if (name === 'subProduct') {
        newData.item = '';
        newData.subItem = '';
      } else if (name === 'item') {
        newData.subItem = '';
      }
      return newData;
    });
  };
  const handleAiFill = async () => {
    if (!formData.productName.trim()) {
      setAiError('Please enter a product name first.');
      return;
    }
    setAiLoading(true);
    setAiError(null);
    try {
      const response = await fetch('https://www.requests.magicpatterns.com/openai/proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'gpt-4-turbo',
          messages: [{
            role: 'system',
            content: `You are a procurement product data assistant. Given a product name, suggest appropriate values for a product catalog entry. Respond ONLY with a valid JSON object (no markdown, no code fences) with these exact keys:
- "product": one of "Laptop", "Printer", "Paper", "Cleaning Supplies"
- "productType": one of "Fixed Asset", "Consumable", "Raw Material", "Service"
- "unitOfMeasure": one of "pcs", "box", "kg", "lot"
- "defaultRate": a reasonable numeric price estimate (number only)
- "warranty": a warranty period string like "1 Year", "6 Months", "No Warranty"
- "notes": a brief 1-2 sentence product description`
          }, {
            role: 'user',
            content: `Product name: "${formData.productName}"`
          }],
          temperature: 0.7,
          max_tokens: 300
        })
      });
      if (!response.ok) {
        throw new Error('Failed to get AI suggestions. Please try again.');
      }
      const data = await response.json();
      const content = data.choices?.[0]?.message?.content;
      if (!content) {
        throw new Error('No suggestions received from AI.');
      }
      const suggestions = JSON.parse(content);
      setFormData((prev: any) => ({
        ...prev,
        product: suggestions.product || prev.product,
        productType: suggestions.productType || prev.productType,
        subProduct: '',
        item: '',
        subItem: '',
        unitOfMeasure: suggestions.unitOfMeasure || prev.unitOfMeasure,
        defaultRate: suggestions.defaultRate?.toString() || prev.defaultRate,
        warranty: suggestions.warranty || prev.warranty,
        notes: suggestions.notes || prev.notes
      }));
    } catch (err: any) {
      setAiError(err.message || 'Something went wrong with AI suggestions.');
    } finally {
      setAiLoading(false);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert(`Product ${mode === 'create' ? 'created' : 'updated'} successfully!`);
    navigate('/products');
  };
  // Get available options based on current selections
  const availableProductTypes = formData.product ? productHierarchy.products.find(p => p.value === formData.product)?.productTypes || [] : [];
  const availableSubProducts = formData.product ? productHierarchy.subProducts[formData.product] || [] : [];
  const availableItems = formData.subProduct ? productHierarchy.items[formData.subProduct] || [] : [];
  const availableSubItems = formData.item ? productHierarchy.subItems[formData.item] || [] : [];
  return <div className="space-y-6 max-w-4xl mx-auto" data-id="element-1430">
      <div className="flex items-center justify-between" data-id="element-1431">
        <div data-id="element-1432">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1433">
            {mode === 'create' ? 'Create Product' : 'Edit Product'}
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-1434">
            Configure product details and specifications.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 md:p-8" data-id="element-1435">
        <FormSection title="Basic Information" data-id="element-1436">
          {mode === 'edit' ? <DisplayField label="Product Code" value={formData.productCode} data-id="element-1437" /> : <TextField label="Product Code" name="productCode" value={formData.productCode} disabled required data-id="element-1438" />}
          <div data-id="element-1439">
            <TextField label="Product Name" name="productName" value={formData.productName} onChange={handleChange} required data-id="element-1440" />
            <div className="mt-2 flex items-center gap-3" data-id="element-1441">
              <button type="button" onClick={handleAiFill} disabled={aiLoading || !formData.productName.trim()} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm" data-id="element-1442">
                {aiLoading ? <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1443" /> : <SparklesIcon className="w-4 h-4" data-id="element-1444" />}
                {aiLoading ? 'Suggesting...' : 'Auto-fill with AI'}
              </button>
              {aiError && <span className="text-sm text-red-600" data-id="element-1445">{aiError}</span>}
            </div>
          </div>

          <SelectField label="Product" name="product" value={formData.product} onChange={handleChange} required options={[{
          value: '',
          label: 'Select Product...'
        }, ...productHierarchy.products.map(p => ({
          value: p.value,
          label: p.label
        }))]} data-id="element-1446" />

          <SelectField label="Product Type" name="productType" value={formData.productType} onChange={handleChange} required disabled={!formData.product} options={[{
          value: '',
          label: 'Select Product Type...'
        }, ...availableProductTypes.map(t => ({
          value: t,
          label: t
        }))]} data-id="element-1447" />

          <SelectField label="Sub Product" name="subProduct" value={formData.subProduct} onChange={handleChange} required disabled={!formData.product} options={[{
          value: '',
          label: 'Select Sub Product...'
        }, ...availableSubProducts.map(sp => ({
          value: sp,
          label: sp
        }))]} data-id="element-1448" />

          <SelectField label="Item" name="item" value={formData.item} onChange={handleChange} required disabled={!formData.subProduct} options={[{
          value: '',
          label: 'Select Item...'
        }, ...availableItems.map(i => ({
          value: i,
          label: i
        }))]} data-id="element-1449" />

          <SelectField label="Sub Item" name="subItem" value={formData.subItem} onChange={handleChange} required disabled={!formData.item} options={[{
          value: '',
          label: 'Select Sub Item...'
        }, ...availableSubItems.map(si => ({
          value: si,
          label: si
        }))]} data-id="element-1450" />

          <SelectField label="Unit of Measure" name="unitOfMeasure" value={formData.unitOfMeasure} onChange={handleChange} required options={[{
          value: 'pcs',
          label: 'pcs'
        }, {
          value: 'box',
          label: 'box'
        }, {
          value: 'kg',
          label: 'kg'
        }, {
          value: 'lot',
          label: 'lot'
        }]} data-id="element-1451" />
          <TextField label="Default Rate" name="defaultRate" type="number" value={formData.defaultRate} onChange={handleChange} data-id="element-1452" />
          <TextField label="Warranty" name="warranty" value={formData.warranty} onChange={handleChange} data-id="element-1453" />
          <SelectField label="Status" name="status" value={formData.status} onChange={handleChange} required options={[{
          value: 'Active',
          label: 'Active'
        }, {
          value: 'Inactive',
          label: 'Inactive'
        }]} data-id="element-1454" />
        </FormSection>

        <FormSection title="Additional Notes" data-id="element-1455">
          <TextAreaField label="Notes / Comments" name="notes" value={formData.notes} onChange={handleChange} rows={3} data-id="element-1456" />
        </FormSection>

        <div className="mt-8 pt-5 border-t border-gray-200 flex justify-end space-x-3" data-id="element-1457">
          <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-1458">
            Cancel
          </button>
          <button type="submit" className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-1459">
            {mode === 'create' ? 'Save Product' : 'Update Product'}
          </button>
        </div>
      </form>
    </div>;
}